import React, { useEffect, useState } from "react";

import { useSelector, useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { RideGrid, RideCheckbox } from "@rimac-seguros/ride-system-components";

import FormContact from "../FormContact/index";
import FormUbigeo from "../FormUbigeo/index";
import FormJob from "../FormJob/index";
import useResponsiveDetection from "@/hooks/useResponsiveDetection";
import { CardDetailPlan } from "../../../shared/components/CardDetailPlan/index";
import { SectionAction } from "../SectionAction/index";
import { cleanToken } from "@/redux/actions/token";
import { resetThirdState } from "@/redux/actions/plans";
import { ModalRightOfWithdrawal } from "../ModalRightOfWithdrawal/index";
import { StateMachine } from "@/utils/consts";
import { setViewModalError } from "@/redux/actions/global";
import helpers, { sendTagChain } from "@/utils/helpers";
import { SectionBeneficiaries } from "@/components/organisms/SectionBeneficiaries/index";
import { getProductOptions } from "@/utils/product";

import "./styles.scss";

const SectionPersonalData = (props) => {
  const {
    register,
    watch,
    setValue,
    trigger,
    setError,
    formState: { errors },
    getValues,
    getFieldState,
  } = useForm({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      streetType: "AV",
    },
  });
  const { planSelected, maxAmountQuote } = useSelector(
    (state) => state.plans
  );
  const { sumAssured } = planSelected || {};
  const [emailWithoutMask, setEmailWithoutMask] = useState();
  const { isSmallScreen } = useResponsiveDetection();
  const dispatch = useDispatch();
  const [showModalExclusions, setShowModalExclusions] = useState(false);
  const { userData, hasTaxObligations, hasAcceptedExclusions } = useSelector((state) => state.vida);
  const { selectedJob, isWorking, company } = useSelector(
    (state) => state.jobs
  );
  const { state } = useSelector((state) => state.global);
  const { beneficiaryType } = useSelector((state) => state.beneficiaries);
  const isCreditMaxAmount = Number(sumAssured?.value) > maxAmountQuote;

  const { digitalDataProductName, productName } = getProductOptions();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      document.body.style.height = `${window.innerHeight}px`;
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const setInitialValues = () => {
    let email = userData?.email ?? userData?.correo;

    if (email) {
      setEmailWithoutMask(email);
    }

    email = email ? helpers.maskEmail(email) : "";
    setValue("email", email);
    setValue("phone", userData.telefono || "");
    setValue("job", selectedJob || "");
    setValue("isWorking", isWorking || "");
    setValue("company", company || "");
    setValue("beneficiaryType", beneficiaryType ?? null);
    setValue("hasTaxObligations", hasTaxObligations ?? "");
    setValue("hasAcceptedExclusions", hasAcceptedExclusions ?? false);
  };

  useEffect(() => {
    dispatch(cleanToken());
    dispatch(resetThirdState());
    setInitialValues();
  }, []);

  useEffect(() => {
    if (userData.indPrevPageApp) {
      helpers.sendMessageToApp(
        showModalExclusions
          ? "EMBEDDED-APP-HIDE-TOPBAR"
          : "EMBEDDED-APP-SHOW-TOPBAR"
      );
    }
  }, [showModalExclusions, userData.indPrevPageApp]);

  useEffect(() => {
    if (state === StateMachine[4]) {
      return dispatch(setViewModalError(true));
    }
  }, [state]);

  const callAnalyticsAction = () => {
    helpers.getTagAction(
      digitalDataProductName,
      "Dirección",
      "click",
      "Ver detalle",
      "trackAction"
    );
  };

  const handleModalExclusions = (open) => {
    helpers.getTagAction(
      digitalDataProductName,
      "Dirección",
      open ? "click" : "Modal Exclusiones",
      open ? "Exclusiones" : "Cerrar",
      "trackAction"
    );
    setShowModalExclusions(open);
  };

  useEffect(() => {
    const virtualName = `sol:comprar:${productName}:direccion`;
    const casuistryName = `Completa datos ${digitalDataProductName}`;
    const { ubigeo } = userData.direccion || {};

    sendTagChain([
      {
        type: "getTagVirtual",
        name: virtualName,
      },
      {
        type: "getTagMetadata",
        key: casuistryName,
        value: ubigeo ? "Con Datos Ubigeo" : "Sin Datos Ubigeo",
      },
    ]);
  }, []);

  return (
    <>
      <div className="section-personal-data">
        <RideGrid>
          <h2 className="section-personal-data_title">
            Completa <span>tus datos</span>
          </h2>
          <div className="col-span-full lg:col-span-7 xl:col-span-6">
            <FormContact
              register={register}
              errors={errors}
              setValue={setValue}
              emailWithoutMask={emailWithoutMask}
              setEmailWithoutMask={setEmailWithoutMask}
            />
            <FormUbigeo
              register={register}
              errors={errors}
              setError={setError}
              setValue={setValue}
              watch={watch}
              isSmallScreen={isSmallScreen}
              loading={loading}
              setLoading={setLoading}
            />
            <FormJob
              register={register}
              errors={errors}
              setError={setError}
              setValue={setValue}
              watch={watch}
              isSmallScreen={isSmallScreen}
            />
            <SectionBeneficiaries
              isVE
              register={register}
              watch={watch}
              errors={errors}
            />
            <RideCheckbox
              {...register("hasAcceptedExclusions", {
                required:  "Este campo es obligatorio",
              })}
              error={errors?.hasAcceptedExclusions}
              errorMessage={errors?.hasAcceptedExclusions?.message}
              size="small"
              className="mt-ride-gl-spacing-4xlarge"
              onClick={() => {
              const isChecked = !getValues("hasAcceptedExclusions");
              if (isChecked) {
                helpers.setActionTag({
                  group: digitalDataProductName,
                  category: "Dirección",
                  action: "click",
                  label: "Checkbox - Aceptar exclusiones",
                })
              }
            }}
            >
              <span className="section-personal-data_exclusions">
                Acepto haber leído las{" "}
                <span onClick={() => handleModalExclusions(true)}>
                  exclusiones.
                </span>
              </span>
            </RideCheckbox>
          </div>
          <div className="hidden lg:block lg:col-span-5 lg:col-start-8">
            {!isCreditMaxAmount && <CardDetailPlan callAnalyticsAction={callAnalyticsAction} />}
          </div>
        </RideGrid>
      </div>
      <SectionAction
        trigger={trigger}
        getFieldState={getFieldState}
        errors={errors}
        data={watch()}
        loading={loading}
        emailWithoutMask={emailWithoutMask}
        {...props}
      />
      {showModalExclusions && (
        <ModalRightOfWithdrawal
          isOpen={showModalExclusions}
          onClose={() => handleModalExclusions(false)}
        />
      )}
    </>
  );
};

export default SectionPersonalData;
